import React, { useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { FormControlLabel, Switch } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { t } from "i18next";
import { useAppConfigStore } from "../../../stores/AppConfig";

const VERSION_PATTERN = /^\d+(\.\d+){1,3}$/;

const APP_TYPES = [
  { key: "userapp", label: "User App" },
  { key: "serviceapp", label: "Service Station App" },
];

const emptyPlatforms = {
  ios: { minVersion: "", forceUpdate: false },
  android: { minVersion: "", forceUpdate: false },
};

const AppVersionForm = ({ appKey, label, config, isSaving, onSave }) => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      minIosVersion: config?.ios?.minVersion ?? "",
      minAndroidVersion: config?.android?.minVersion ?? "",
      forceUpdateIos: config?.ios?.forceUpdate ?? false,
      forceUpdateAndroid: config?.android?.forceUpdate ?? false,
    },
    validationSchema: Yup.object({
      minIosVersion: Yup.string()
        .required(t("Required"))
        .matches(VERSION_PATTERN, t("Use a dotted version like 1.3 or 1.0.15")),
      minAndroidVersion: Yup.string()
        .required(t("Required"))
        .matches(VERSION_PATTERN, t("Use a dotted version like 1.3 or 1.0.15")),
      forceUpdateIos: Yup.boolean(),
      forceUpdateAndroid: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      try {
        await onSave({
          [appKey]: {
            ios: {
              minVersion: values.minIosVersion,
              forceUpdate: values.forceUpdateIos,
            },
            android: {
              minVersion: values.minAndroidVersion,
              forceUpdate: values.forceUpdateAndroid,
            },
          },
        });
        toast.success(t("App version settings saved"));
      } catch (error) {
        // Error toast is handled by the axios interceptor
      }
    },
  });

  return (
    <Card>
      <CardHeader>
        <h4 className="card-title mb-0">{t(label)}</h4>
      </CardHeader>
      <CardBody>
        <form onSubmit={formik.handleSubmit}>
          <h5 className="mb-3">{t("iOS")}</h5>
          <div className="mb-3">
            <Label htmlFor={`${appKey}-minIosVersion`} className="form-label">
              {t("Minimum iOS Version")}
            </Label>
            <Input
              id={`${appKey}-minIosVersion`}
              name="minIosVersion"
              type="text"
              placeholder="1.3"
              value={formik.values.minIosVersion}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.minIosVersion && Boolean(formik.errors.minIosVersion)
              }
            />
            {formik.touched.minIosVersion && formik.errors.minIosVersion && (
              <div className="text-danger mt-1">{formik.errors.minIosVersion}</div>
            )}
          </div>
          <div className="mb-4">
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.forceUpdateIos}
                  onChange={(event) =>
                    formik.setFieldValue("forceUpdateIos", event.target.checked)
                  }
                />
              }
              label={t("Force iOS update")}
            />
          </div>

          <h5 className="mb-3">{t("Android")}</h5>
          <div className="mb-3">
            <Label htmlFor={`${appKey}-minAndroidVersion`} className="form-label">
              {t("Minimum Android Version")}
            </Label>
            <Input
              id={`${appKey}-minAndroidVersion`}
              name="minAndroidVersion"
              type="text"
              placeholder="1.0.15"
              value={formik.values.minAndroidVersion}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.minAndroidVersion &&
                Boolean(formik.errors.minAndroidVersion)
              }
            />
            {formik.touched.minAndroidVersion &&
              formik.errors.minAndroidVersion && (
                <div className="text-danger mt-1">
                  {formik.errors.minAndroidVersion}
                </div>
              )}
          </div>
          <div className="mb-4">
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.forceUpdateAndroid}
                  onChange={(event) =>
                    formik.setFieldValue("forceUpdateAndroid", event.target.checked)
                  }
                />
              }
              label={t("Force Android update")}
            />
          </div>

          <Button color="primary" type="submit" disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("Saving")}
              </>
            ) : (
              t("Save")
            )}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
};

const AppVersion = () => {
  document.title = "App Version | Clickorder Admin";

  const { config, isLoading, savingType, getVersionConfig, updateVersionConfig } =
    useAppConfigStore((state) => state);

  useEffect(() => {
    getVersionConfig();
  }, []);

  return (
    <Row>
      {isLoading && !config ? (
        <Col>
          <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        </Col>
      ) : (
        APP_TYPES.map(({ key, label }) => (
          <Col key={key} lg={6} className="mb-4">
            <AppVersionForm
              appKey={key}
              label={label}
              config={config?.[key] || emptyPlatforms}
              isSaving={savingType === key}
              onSave={updateVersionConfig}
            />
          </Col>
        ))
      )}
    </Row>
  );
};

export default AppVersion;
